/**
 * Trinity System - Main Entry Point
 *
 * Unified playground module replacing Coordination, Mosaic, Image-to-Triangles,
 * and Icon Editor with a single tri-cube canvas system.
 */

import { TrinityEngine } from './core/trinity-engine';
import { PatternGallery } from './core/pattern-gallery';
import type { TriangleSize, PatternFn, PatternData } from './core/types';
import { TRIANGLE_SIZE_PRESETS, TRINITY_PALETTE } from './core/types';

// ---------------------------------------------------------------------------
// DOM
// ---------------------------------------------------------------------------

const canvas = document.getElementById('canvas') as HTMLCanvasElement;
const galleryPanel = document.getElementById('gallery-panel') as HTMLElement;
const coordDisplay = document.getElementById('coord-display') as HTMLElement;
const sizeDisplay = document.getElementById('size-display') as HTMLElement;
const triangleCountEl = document.getElementById('triangle-count') as HTMLElement;

const toolBtns = document.querySelectorAll('#toolbar .tool-btn[data-tool]') as NodeListOf<HTMLButtonElement>;
const colorDots = document.querySelectorAll('#toolbar .color-dot') as NodeListOf<HTMLElement>;
const sizeBtns = document.querySelectorAll('.size-btn') as NodeListOf<HTMLButtonElement>;
const modeBtns = document.querySelectorAll('.mode-btn') as NodeListOf<HTMLButtonElement>;
const clearBtn = document.getElementById('clear-btn') as HTMLButtonElement;
const saveBtn = document.getElementById('save-btn') as HTMLButtonElement;
const exportBtn = document.getElementById('export-btn') as HTMLButtonElement;
const exportSvgBtn = document.getElementById('export-svg-btn') as HTMLButtonElement;
const toggleGalleryBtn = document.getElementById('toggle-gallery') as HTMLButtonElement;
const gridRadiusInput = document.getElementById('grid-radius') as HTMLInputElement;

// ---------------------------------------------------------------------------
// Engine & Gallery
// ---------------------------------------------------------------------------

const engine = new TrinityEngine(canvas);
const gallery = new PatternGallery(galleryPanel);

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let currentTool: 'paint' | 'erase' | 'fill' | 'select' = 'paint';
let currentColor: string = TRINITY_PALETTE.lightBlue;
let currentMode: 'draw' | 'pattern' | 'image' = 'draw';
let galleryVisible = true;

// ---------------------------------------------------------------------------
// Engine hooks
// ---------------------------------------------------------------------------

engine.setOnHover((coord) => {
  if (coord) {
    coordDisplay.textContent = `(${coord.a}, ${coord.b}, ${coord.c})`;
  } else {
    coordDisplay.textContent = '\u2014';
  }
  triangleCountEl.textContent = String(engine.triangleCount);
});

// Engine handles its own mouse events internally.
// Color is synced via engine.setColor() when palette changes.

// ---------------------------------------------------------------------------
// Tool buttons
// ---------------------------------------------------------------------------

toolBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    currentTool = btn.dataset.tool as typeof currentTool;
    engine.setTool(currentTool);
    toolBtns.forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
  });
});

// ---------------------------------------------------------------------------
// Color palette
// ---------------------------------------------------------------------------

colorDots.forEach((dot) => {
  dot.addEventListener('click', () => {
    currentColor = dot.dataset.color || TRINITY_PALETTE.lightBlue;
    engine.setColor(currentColor);
    colorDots.forEach((d) => d.classList.remove('active'));
    dot.classList.add('active');
  });
});

// ---------------------------------------------------------------------------
// Size selector
// ---------------------------------------------------------------------------

sizeBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    const size = btn.dataset.size as TriangleSize;
    engine.setSize(size);
    sizeBtns.forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    sizeDisplay.textContent = `${size} (${TRIANGLE_SIZE_PRESETS[size]}px)`;
  });
});

// ---------------------------------------------------------------------------
// Mode selector
// ---------------------------------------------------------------------------

modeBtns.forEach((btn) => {
  btn.addEventListener('click', () => {
    currentMode = btn.dataset.mode as typeof currentMode;
    modeBtns.forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
  });
});

// ---------------------------------------------------------------------------
// Clear / Export
// ---------------------------------------------------------------------------

clearBtn?.addEventListener('click', () => engine.clear());

saveBtn?.addEventListener('click', () => {
  const name = prompt('Pattern name:');
  if (!name) return;
  const desc = prompt('Description (optional):') || '';
  const pattern = engine.exportPattern();
  pattern.name = name;
  pattern.description = desc;
  gallery.savePattern(pattern);
});

exportBtn?.addEventListener('click', () => {
  const dataUrl = engine.exportPNG();
  const link = document.createElement('a');
  link.download = 'trinity-pattern.png';
  link.href = dataUrl;
  link.click();
});

exportSvgBtn?.addEventListener('click', () => {
  const svg = engine.exportSVG();
  if (!svg) return;
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.download = 'trinity-pattern.svg';
  link.href = url;
  link.click();
  URL.revokeObjectURL(url);
});

// ---------------------------------------------------------------------------
// Grid radius slider
// ---------------------------------------------------------------------------

gridRadiusInput?.addEventListener('input', () => {
  const r = Number(gridRadiusInput.value);
  if (r > 0) engine.setGridRadius(r);
});

// ---------------------------------------------------------------------------
// Gallery toggle
// ---------------------------------------------------------------------------

toggleGalleryBtn?.addEventListener('click', () => {
  galleryVisible = !galleryVisible;
  galleryPanel.classList.toggle('collapsed', !galleryVisible);
});

// ---------------------------------------------------------------------------
// Gallery callbacks
// ---------------------------------------------------------------------------

gallery.setOnSelect((pattern: PatternData) => {
  engine.importPattern(pattern);
});

gallery.setOnApplyFunction((body: string) => {
  try {
    const fn = new Function('a', 'b', 'c', `return ${body}`) as PatternFn;
    // Test it with a sample call
    fn(1, 1, 0);
    engine.clear();
    engine.applyPattern(fn);
  } catch (err) {
    console.error('Invalid pattern function:', err);
  }
});

// ---------------------------------------------------------------------------
// Keyboard shortcuts
// ---------------------------------------------------------------------------

window.addEventListener('keydown', (e: KeyboardEvent) => {
  if ((e.target as HTMLElement).tagName === 'INPUT' || (e.target as HTMLElement).tagName === 'TEXTAREA') return;

  switch (e.key) {
    case 'p':
      currentTool = 'paint';
      engine.setTool('paint');
      toolBtns.forEach((b) => b.classList.toggle('active', b.dataset.tool === 'paint'));
      break;
    case 'e':
      currentTool = 'erase';
      engine.setTool('erase');
      toolBtns.forEach((b) => b.classList.toggle('active', b.dataset.tool === 'erase'));
      break;
    case 'f':
      currentTool = 'fill';
      engine.setTool('fill');
      toolBtns.forEach((b) => b.classList.toggle('active', b.dataset.tool === 'fill'));
      break;
    case 's':
      if (e.ctrlKey || e.metaKey) return;
      currentTool = 'select';
      engine.setTool('select');
      toolBtns.forEach((b) => b.classList.toggle('active', b.dataset.tool === 'select'));
      break;
    case 'g':
      toggleGalleryBtn?.click();
      break;
    case '1': case '2': case '3': case '4': case '5': {
      const idx = Number(e.key) - 1;
      const dots = Array.from(colorDots);
      if (idx < dots.length) {
        currentColor = dots[idx].dataset.color || currentColor;
        engine.setColor(currentColor);
        colorDots.forEach((d, i) => d.classList.toggle('active', i === idx));
      }
      break;
    }
    case 'Escape':
      engine.clear();
      break;
    case 'c':
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        engine.clear();
      }
      break;
  }
});

// ---------------------------------------------------------------------------
// Resize
// ---------------------------------------------------------------------------

function handleResize(): void {
  engine.resize();
}

window.addEventListener('resize', handleResize);
handleResize();

// ---------------------------------------------------------------------------
// Initial load
// ---------------------------------------------------------------------------

gallery.fetchPatterns();

console.log('Trinity System loaded');
